import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, extractError } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { AlertTriangle, Loader2, Trash2, Eraser } from "lucide-react";

const LABELS = ["enrollments", "active_enrollments", "payments", "measurements", "journal_posts", "comments", "reactions", "journal_points", "recognition_pins"];

/** Master Admin force-delete dialog for a batch or a whole program. */
export function ForceDeleteDialog({ kind, id, name, onClose, onDone }) {
  const { t } = useLang();
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get(`/admin/${kind === "batch" ? "batches" : "programs"}/${id}/delete-preview`)
      .then(({ data }) => setPreview(data.data))
      .catch((e) => { toast.error(extractError(e).message); onClose(); });
  }, [kind, id, onClose]);

  const word = preview?.confirm_word || "";
  const ready = confirm.trim().toUpperCase() === word.trim().toUpperCase() && word.length > 0;

  const run = async (mode) => {
    setBusy(mode);
    try {
      if (mode === "clear") {
        const { data } = await api.post(`/admin/batches/${id}/clear`, { confirm });
        toast.success(t("del.cleared").replace("{n}", data.data.removed.enrollments));
      } else {
        const path = kind === "batch" ? `/admin/batches/${id}/force` : `/admin/programs/${id}/force`;
        await api.delete(`${path}?confirm=${encodeURIComponent(confirm)}`);
        toast.success(t("del.deleted"));
      }
      onDone();
      onClose();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="force-delete-dialog" className="bg-[#10121B] border-[#FF3B30]/30 text-slate-200 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#FF8A80]" />
            {kind === "batch" ? t("del.batchTitle") : t("del.programTitle")}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">{t("del.warning")}</DialogDescription>
        </DialogHeader>

        {!preview ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">{name}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {LABELS.filter((k) => preview.counts[k] !== undefined).map((k) => (
                <div key={k} className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{t(`del.c_${k}`)}</div>
                  <div data-testid={`del-count-${k}`} className="font-[Outfit] text-lg font-extrabold text-white">{preview.counts[k]}</div>
                </div>
              ))}
              {kind === "program" && (
                <div className="rounded-lg border border-white/5 bg-white/[0.03] p-2.5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("del.c_batches")}</div>
                  <div data-testid="del-count-batches" className="font-[Outfit] text-lg font-extrabold text-white">{preview.counts.batches}</div>
                </div>
              )}
            </div>

            {(preview.members || []).length > 0 && (
              <div data-testid="del-member-list" className="max-h-32 overflow-y-auto space-y-1 rounded-lg border border-white/5 p-2">
                {preview.members.map((m) => (
                  <div key={m.enrollment_id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-slate-300 truncate">{m.full_name || m.email}</span>
                    <span className="text-slate-500">{m.status}</span>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-[#FF8A80] sr-only">{t("del.warning")}</p>
            <div className="space-y-1.5">
              <label className="text-sm text-slate-300">{t("del.typeToConfirm").replace("{word}", word)}</label>
              <Input data-testid="del-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder={word} className="bg-[#1C202E] border-white/10 text-white h-10" />
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button data-testid="del-cancel-btn" variant="ghost" onClick={onClose} className="text-slate-400 hover:bg-white/5">{t("common.cancel")}</Button>
              {kind === "batch" && (
                <Button data-testid="del-clear-btn" disabled={!ready || !!busy} onClick={() => run("clear")}
                  className="bg-white/10 hover:bg-white/15 text-white font-semibold">
                  {busy === "clear" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eraser className="h-4 w-4 mr-1" /> {t("del.clearBatch")}</>}
                </Button>
              )}
              <Button data-testid="del-force-btn" disabled={!ready || !!busy} onClick={() => run("force")}
                className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
                {busy === "force" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> {t("del.forceDelete")}</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
